<template>
  <div class="local-line-item">

    <div class="order-item-name lead fw-bold overflow-auto" style="height:70px" @click.prevent="showItem=true" role="button">
      {{ item.item_name }}
    </div>

    <div class="mb-3">
      <strong v-if="item.sale_price && item.sale_price !== item.unit_price"
              class="display-6 text-muted me-3">
        <strike>${{ parseFloat(item.sale_price).toFixed(2) }}</strike>
      </strong>
      <strong class="display-6 text-success">
        ${{ parseFloat(item.unit_price).toFixed(2) }}
      </strong>
      <small v-if="item.quantity > 1" class="mx-2 text-muted lead">
        &times; {{ item.quantity }}
      </small>
    </div>

    <order-gallery
      v-if="item.photo_url"
      :order-img="item.photo_url"
      :remote-id="item.remote_id"
    />

    <div class="mt-2">
      <small class="text-muted d-block"><ebay-category :category-id="item.ebay_category_id" /></small>
      <small class="text-muted">Source:
        <a href="#" @click.prevent="updateSource">
        <strong>{{ sourceCategoryName }}</strong>
      </a></small>
        <app-modal :modal-in="showSource" @close="showSource = false" modal-class="modal-md">
          <template #header>Source Details</template>
          <item-source-edit
              :item="item"
              class="mb-3"
              @update:source-price="onSourcePriceUpdated"
              @update:acquisition-date="onAcquisitionDateUpdated"
              @update:source-data="onSourceDataUpdated"
          />
          <div class="p-5 mb-5"></div>
        </app-modal>

    </div>

    <div v-if="!discreet" class="mt-3 row small">
      <div class="col-12 col-md-6 mb-3 mb-md-0">
        <div class="p-1 rounded bg-info-subtle">
          <div>ID: <strong>{{ item.remote_id }}</strong></div>
          <div>SKU: <strong>{{ item.SKU }}</strong></div>
          <div v-if="item.days_listed">
            Days Listed: <strong>{{ item.days_listed }}</strong>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="fw-bold">Quantity X {{ item.quantity }}</div>
        <div class="d-flex">
          <div class="me-3 fw-bold">Unit Price</div>
          <div class="ms-auto">${{ parseFloat(item.unit_price).toFixed(2) }}</div>
        </div>
        <div v-if="item.quantity > 1" class="d-flex">
          <div class="me-3 fw-bold">Line Total</div>
          <div class="ms-auto fw-bold">${{ (parseFloat(item.unit_price) * item.quantity).toFixed(2) }}</div>
        </div>
        <div v-if="item.source_price" class="d-flex mt-2 pt-2 border-top">
          <div class="me-3 fw-bold">Source Price</div>
          <div class="ms-auto">${{ parseFloat(item.source_price).toFixed(2) }}</div>
        </div>
        <div v-if="item.source_price" class="d-flex">
          <div class="me-3 fw-bold">Profit</div>
          <div class="ms-auto text-success fw-bold">
            ${{ ((parseFloat(item.unit_price) - parseFloat(item.source_price)) * item.quantity).toFixed(2) }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <inventory-detail-modal v-model="showItem" :item="item" :lean="false" />
</template>

<script setup>
  import { ref, defineProps, defineEmits, computed } from "vue"
  import { useStore } from "vuex"
  import OrderGallery from "@/app/components/orders/OrderGallery.vue"
  import InventoryDetailModal from "@/app/components/inventory/InventoryDetailModal.vue";
  import ItemSourceEdit from "@/app/components/inventory/ItemSourceEdit.vue";
  import AppModal from "@/app/components/Utils/AppModal.vue";
  import EbayCategory from "@/app/components/ebay/EbayCategory.vue";

  const store = useStore();
  const showItem = ref(false)
  const showSource = ref(false)

  const props = defineProps({
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    discreet: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['update:item'])

  const sourceCategory = computed(() => {
    return props.item.source_category || sourceCategories.value.flatMap(sc => sc.subcategories || []).find(sub => sub.id === props.item.source_category_id)?.name || ''
  })

  const sourceLocation = computed(() => {
    return props.item.source_location || sourceLocations.value.flatMap(sl => sl.locations || []).find(sub => sub.id === props.item.source_location_id)?.name || ''
  })

  const sourceCategories = computed(() => store.state.eshop.sourceCategories || [])

  const sourceLocations = computed(() => {
    const locations = store.state.eshop.sourceLocations || []
    return locations.filter(category => category.locations && category.locations.length > 0)
  })

  const sourceCategoryName = computed(() => {
    if (!sourceCategory.value && !sourceLocation.value) return 'Uncategorized'
    return `${sourceCategory.value} ${sourceCategory.value ? '/' : ''} ${sourceLocation.value}`.trim()
  })

  function onSourcePriceUpdated(price) {
    emit('update:item', { ...props.item, source_price: price })
  }

  function onAcquisitionDateUpdated(date) {
    emit('update:item', { ...props.item, acquisition_date: date })
  }

  function onSourceDataUpdated(sourceData) {
    emit('update:item', {
      ...props.item,
      source_category_id: sourceData.source_category_id,
      source_location_id: sourceData.source_location_id
    })
  }

  function updateSource() {
    showSource.value = true
  }
</script>

<style lang="scss" scoped>
.local-line-item {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px dotted #343a40;
  // Add any custom styling here
}
</style>
